import { ArrowBigLeft, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/ux-common/components/button/button';
import { cn } from '@/ux-common/lib/utils';

import { FIELD_INTERACTIVE_SELECTOR } from './input.constants';
import {
  InputFieldContext,
  type InputFieldRegistration,
  InputGroupContext,
  type InputState,
  useGroupField,
} from './input.context';
import { slotName } from './split-trigger-children';

/**
 * Input — composable text field with a CSS class API.
 *
 * `Input` is the **`.input` group** (a thin structural wrapper); `InputField` is the
 * **bordered `.input-field` with the `<input class="input-control">` embedded
 * automatically**. There is no behavior compound — value/onChange/ref go straight to
 * the embedded native `<input>`, and label / helper / clear are pure composition.
 *
 * Composition:
 *
 * ```tsx
 * <Input>
 *   <InputLabel htmlFor="name">Name</InputLabel>
 *   <InputField id="name" value={v} onChange={(e) => setV(e.target.value)} ref={ref}>
 *     <InputLeadingIcon>
 *       <Search />
 *     </InputLeadingIcon>
 *     {v && <InputClear onClick={() => setV('')} />}
 *   </InputField>
 *   <InputHelper>Optional helper text</InputHelper>
 * </Input>
 * ```
 *
 * Renders:
 *
 * ```html
 * <div class="input">
 *   <label class="input-label" for="name">Name</label>
 *   <div class="input-field">
 *     <input class="input-control" id="name" … />
 *     <span class="input-leading-icon">…</span>   <!-- order:-1 → left -->
 *     <button class="input-clear">…</button>       <!-- margin-auto → right -->
 *   </div>
 *   <div class="input-helper">Optional helper text</div>
 * </div>
 * ```
 *
 * Size and state are classes on the root `.input` (`.input-lg`/`.input-md`,
 * `.input-error`/`.input-warning`/`.input-new`/`.input-display`/`.input-inverse`).
 * All visual styling lives in the theme (`themes/default/components/input.css` owns
 * the API; `titan` rebinds `--input-*` to `--ckds-*`). The bordered chrome is on the
 * `.input-field`; focus is `:focus-within`, disabled is `:has(input:disabled)`.
 */

/* ============================================================================
   Input — the `.input` group
   ========================================================================== */

export interface InputProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The group's validation / edit state. Applies the matching root class and
   * publishes the value on the group context, so a slot rendered inside `InputField`
   * — which cannot see the root's class from where it sits — can read it instead of
   * being told twice.
   *
   * `className="input-error"` remains valid and unchanged; it simply does not reach
   * the context. Set one or the other, not both.
   */
  state?: InputState;
}

/**
 * An ordered list that entries join on mount and leave on unmount — the shape the
 * group keeps its fields, helpers and labels in. `register` appends and returns the
 * unregister callback.
 *
 * An array rather than just "the first", so the primary hands over correctly when
 * the first entry unmounts. Unregistering removes ONE occurrence, not every equal
 * value: a consumer may give two helpers the same explicit id, and filtering by
 * value would strip both when either unmounted, leaving the control with no
 * description at all.
 */
function useRegistrationList<T>() {
  const [items, setItems] = React.useState<T[]>([]);
  const register = React.useCallback((item: T) => {
    setItems((current) => [...current, item]);
    return () =>
      setItems((current) => {
        const at = current.indexOf(item);
        return at === -1 ? current : [...current.slice(0, at), ...current.slice(at + 1)];
      });
  }, []);
  return [items, register, setItems] as const;
}

export const Input = React.forwardRef<HTMLDivElement, InputProps>(
  ({ className, children, state, ...props }, ref) => {
    // Fields in registration order: the first is the primary `InputLabel` points at.
    const [fields, registerField, setFields] = useRegistrationList<InputFieldRegistration>();

    // Patch in place rather than re-register. `registerField` appends, so carrying a
    // changed `disabled` through it would move the field to the end of the list and
    // make a peer the primary. Bails when nothing changed, so a field re-rendering
    // with the same values does not produce a new array and a new context value.
    const updateField = React.useCallback(
      (controlId: string, patch: Partial<InputFieldRegistration>) => {
        setFields((current) => {
          const at = current.findIndex((f) => f.controlId === controlId);
          if (at === -1) return current;
          const merged = { ...current[at], ...patch };
          const unchanged = (Object.keys(patch) as (keyof InputFieldRegistration)[]).every(
            (key) => current[at][key] === merged[key]
          );
          if (unchanged) return current;
          return [...current.slice(0, at), merged, ...current.slice(at + 1)];
        });
      },
      // A React state setter, stable for the component's life; listed for the lint
      // rule only, so `updateField` keeps one identity.
      [setFields]
    );

    // Helpers register their EFFECTIVE id, so a consumer-supplied id on the
    // helper is what the control ends up pointing at. Registering a count and
    // assuming the generated id would silently dangle in exactly that case.
    const [describedByIds, registerHelper] = useRegistrationList<string>();

    // Only the first label is published: a group has one field label, and a
    // second is a consumer's own markup.
    const [labelIds, registerLabel] = useRegistrationList<string>();

    const primary = fields[0];
    const labelId = labelIds[0];
    const value = React.useMemo(
      () => ({
        controlId: primary?.controlId,
        controlRef: primary?.controlRef,
        controlDisabled: Boolean(primary?.disabled),
        controlReadOnly: Boolean(primary?.readOnly),
        describedByIds,
        registerHelper,
        labelId,
        registerLabel,
        registerField,
        updateField,
        state,
      }),
      [
        primary,
        describedByIds,
        registerHelper,
        labelId,
        registerLabel,
        registerField,
        updateField,
        state,
      ]
    );

    return (
      <InputGroupContext.Provider value={value}>
        <div
          ref={ref}
          data-component="Input"
          className={cn('input', state && `input-${state}`, className)}
          {...props}
        >
          {children}
        </div>
      </InputGroupContext.Provider>
    );
  }
);
Input.displayName = 'Input';

/* ============================================================================
   InputField — `.input-field` with an embedded `.input-control`
   ========================================================================== */

export interface InputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Class(es) for the field container (border/layout). */
  className?: string;
  /** Inline style for the field container. */
  style?: React.CSSProperties;
  /** Class(es) for the embedded `<input>` control. */
  controlClassName?: string;
  /**
   * Size the control to its text content so a trailing `InputUnit` /
   * `InputValueChange` hugs the value with constant spacing and shifts as you type.
   * Auto-enabled when the children include an `InputUnit` or `InputValueChange`;
   * pass `false` to force it off, `true` to force it on.
   *
   * Always off for `type="password"`, and `true` does not override that: `size` is a
   * plain DOM attribute, so it survives value masking and would publish a live
   * character count of the secret.
   */
  autoSize?: boolean;
  /**
   * Refuses the field and says why — a view-only session, a missing permission.
   * Unlike `disabled`, the control stays in the Tab order (a disabled input takes no
   * focus, so a keyboard user never reaches it or its reason). It reads and looks
   * disabled (`aria-disabled`), is read-only underneath — no typing, paste or drop,
   * the value still selectable to copy — its `InputStepper` goes inert and its
   * `InputClear` is not rendered, and the reason is announced as its description.
   * Wins over `disabled` when both are set.
   */
  disabledReason?: React.ReactNode;
  /** Slots rendered inside the field alongside the control (icon, unit, clear, affordance). */
  children?: React.ReactNode;
}

/**
 * InputField — the bordered flex shell. Renders `<div class="input-field">` with an
 * embedded `<input class="input-control">`; the forwarded `ref` and all native input
 * attributes (`value`, `onChange`, `placeholder`, `type`, `disabled`, `id`, `aria-*`,
 * …) go to that `<input>`. `className` and `style` target the field container;
 * `controlClassName` targets the control. `children` are extra slots placed inside
 * the field.
 *
 * When a unit / value-change rides beside the value, the control auto-sizes to its
 * text so the trailing slot hugs the value with constant spacing and shifts as you
 * type. The exact fit comes from CSS `field-sizing: content`; the native `size`
 * attribute (tracking the value's character count) is the fallback for engines
 * without it. Either way there is no per-keystroke layout measurement.
 *
 * The whole field is a click target: a native `<input>` only takes focus on a direct
 * hit, so pressing the field's padding — or the unit / value-change beside a
 * shrunk control — is redirected to the control (caret at the start when the press
 * lands before it, otherwise at the end). Presses on the clear button, the info
 * trigger, or any other interactive slot are left alone.
 */
export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      className,
      style,
      controlClassName,
      autoSize,
      children,
      type = 'text',
      onInput,
      id,
      'aria-describedby': ariaDescribedBy,
      disabledReason,
      ...inputProps
    },
    ref
  ) => {
    // A refused field is read-only underneath, never natively disabled: a disabled
    // input takes no focus, and the point is that a keyboard user reaches it and
    // hears why. Read-only blocks typing, paste and drop natively and inerts the
    // stepper; `aria-disabled` makes it read and look disabled.
    const reasonId = React.useId();
    const refused = disabledReason != null && disabledReason !== false && disabledReason !== '';

    // `disabled` / `readOnly` ride in `inputProps` on their way to the <input>. Read
    // without removing them: the control still needs both.
    const disabled = !refused && Boolean(inputProps.disabled);
    const readOnly = refused || Boolean(inputProps.readOnly);

    // Own id and control handle, registration with the group, and the merged
    // description — the shared rules every field type follows (see useGroupField).
    // `fieldContext` is published to the field's own children, so slots resolve the
    // control beside them rather than the group's primary.
    const {
      controlId,
      controlRef,
      setControlRef,
      describedBy,
      field: fieldContext,
    } = useGroupField<HTMLInputElement>({
      id,
      ref,
      // Refused reads as disabled to the group, as it does on the triggers.
      disabled: disabled || refused,
      readOnly,
      ariaDescribedBy,
    });

    // Auto-enable content sizing when a unit / value-change slot is present —
    // matched by name, not identity, so a vendored copy still matches (see slotName).
    let hasAutoSizeSlot = false;
    React.Children.forEach(children, (child) => {
      if (hasAutoSizeSlot || !React.isValidElement(child)) return;
      const name = slotName(child);
      if (name === 'InputUnit' || name === 'InputValueChange') hasAutoSizeSlot = true;
    });
    // Never on a password field, and not overridable by the `autoSize` prop. `size`
    // is a plain DOM attribute, so it survives the masking that browser extensions
    // and session-replay tools apply to `value` — auto-sizing would publish a live
    // character count of the secret, updated on every keystroke.
    const isSecret = type === 'password';
    const shouldAutoSize = !isSecret && (autoSize ?? hasAutoSizeSlot);

    // Auto-width: drive the native `size` attribute from the value's character count
    // so the control tracks its text and a trailing unit / value-change hugs the value
    // with constant spacing and shifts as you type. Deterministic + cross-browser.
    //
    // Controlled fields DERIVE the count during render rather than mirroring the
    // value into state. Mirroring cost a second render pass per keystroke — and
    // for a plain controlled field, one that produced no DOM at all, since `size`
    // is only emitted when auto-sizing. It was also wrong: a parent that pins
    // `value` and ignores `onChange` got a box that grew as you typed while the
    // displayed value never changed. State is kept only for the uncontrolled
    // case, where there is no prop to read. Same shape as
    // `use-selection-group.ts`.
    const isControlled = inputProps.value !== undefined;
    const [uncontrolledCount, setUncontrolledCount] = React.useState(() =>
      Math.max(String(inputProps.defaultValue ?? '').length, 1)
    );
    const charCount = isControlled
      ? Math.max(String(inputProps.value ?? '').length, 1)
      : uncontrolledCount;

    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
      if (shouldAutoSize && !isControlled) {
        setUncontrolledCount(Math.max(event.currentTarget.value.length, 1));
      }
      onInput?.(event);
    };

    // The control fills the field only when it isn't auto-sized, and never covers the
    // field's padding — so pressing anywhere else in the field would otherwise do
    // nothing. Redirect that press to the control, on mousedown so focus lands before
    // the browser would move it elsewhere.
    const handleFieldMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      // Primary button only. This handler calls preventDefault() and moves the
      // caret, so on a right-click it would steal focus and collapse a selection the
      // user was about to act on through the context menu, and on X11 it would block
      // a middle-click primary-selection paste.
      if (event.button !== 0) return;

      const control = controlRef.current;
      // `readOnly` is excluded alongside `disabled`: consumers use it to mean "no
      // permission to edit" (disabled controls do not fire the pointer events a
      // permission tooltip needs), so focusing one advertises editability it does
      // not have. The `preventDefault()` below would also suppress the drag-select
      // that is the one useful interaction on a read-only value.
      if (!control || control.disabled || control.readOnly) return;

      const field = event.currentTarget;
      const target = event.target as HTMLElement | null;
      if (!target || target === control) return; // direct hit — let the browser do it

      // `closest()` walks past the field, so bound the match to it: an interactive
      // ancestor *outside* the field (a Radix overlay's `tabindex="-1"` wrapper) is
      // not the user pressing a slot.
      const interactive = target.closest(FIELD_INTERACTIVE_SELECTOR);
      if (interactive && field.contains(interactive)) return; // clear button, info trigger, …

      // Suppress the default press behavior (focusing the field, starting a text
      // selection) and hand focus to the control instead.
      event.preventDefault();
      control.focus();

      const caret = event.clientX < control.getBoundingClientRect().left ? 0 : control.value.length;
      try {
        control.setSelectionRange(caret, caret);
      } catch {
        // Types like number/email/date don't expose a selection — focus is enough.
      }
    };

    return (
      // The field is not an interactive element and gets no role: the handler only
      // widens the pointer target for the embedded <input>, which remains the sole
      // focusable, keyboard-operable control. Nothing here is mouse-exclusive.
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        data-component="InputField"
        className={cn('input-field', className)}
        style={style}
        onMouseDown={handleFieldMouseDown}
      >
        <input
          ref={setControlRef}
          type={type}
          id={controlId}
          aria-describedby={
            [refused ? reasonId : undefined, describedBy].filter(Boolean).join(' ') || undefined
          }
          data-component="InputControl"
          className={cn(
            'input-control',
            shouldAutoSize && 'input-control-autosize',
            controlClassName
          )}
          {...inputProps}
          {...(refused && { disabled: false, readOnly: true, 'aria-disabled': true })}
          size={shouldAutoSize ? charCount : undefined}
          onInput={handleInput}
        />
        <InputFieldContext.Provider value={fieldContext}>
          {/* A refused field has nothing to clear, and the clear button's onClick is
              the app's — it would edit the value the field refuses. */}
          {refused
            ? React.Children.map(children, (child) =>
                slotName(child) === 'InputClear' ? null : child
              )
            : children}
        </InputFieldContext.Provider>
        {refused && (
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        )}
      </div>
    );
  }
);
InputField.displayName = 'InputField';

/* ============================================================================
   Slot primitives
   ========================================================================== */

/** InputControl — the bare borderless `<input class="input-control">` primitive.
 *  Only needed when hand-building a field without `InputField`. */
export type InputControlProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;
export const InputControl = React.forwardRef<HTMLInputElement, InputControlProps>(
  ({ className, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      data-component="InputControl"
      className={cn('input-control', className)}
      {...props}
    />
  )
);
InputControl.displayName = 'InputControl';

/**
 * InputLeadingIcon — leading icon slot. Renders a Lucide icon at the field's icon
 * size (20px). It uses `order: -1`, so it renders on the leading edge even though it
 * sits after the embedded control in the DOM. Compose `.input-leading-icon-sm` via
 * className for the 16px DataSpan-set size.
 *
 * Takes the icon as `children`, matching `InputTrailingIcon` 25 lines below and the
 * rest of the registry. It used to take an `icon` element-type prop — the only such
 * prop in the registry — which meant one file had two call shapes for one job.
 */
export type InputLeadingIconProps = React.HTMLAttributes<HTMLSpanElement>;
export const InputLeadingIcon = React.forwardRef<HTMLSpanElement, InputLeadingIconProps>(
  ({ className, children, ...props }, ref) => (
    <span
      ref={ref}
      data-component="InputLeadingIcon"
      className={cn('input-leading-icon', className)}
      aria-hidden="true"
      {...props}
    >
      {children}
    </span>
  )
);
InputLeadingIcon.displayName = 'InputLeadingIcon';

/** InputUnit — trailing unit/qualifier slot (e.g. `°F`, `kW`). */
export type InputUnitProps = React.HTMLAttributes<HTMLSpanElement>;
export const InputUnit = React.forwardRef<HTMLSpanElement, InputUnitProps>(
  ({ className, ...props }, ref) => (
    <span ref={ref} data-component="InputUnit" className={cn('input-unit', className)} {...props} />
  )
);
InputUnit.displayName = 'InputUnit';

/**
 * InputClear — the clear button. Composes a registry Button (circle, xs, default). Wire `onClick`.
 *
 * `type` is omitted and pinned to `"button"`. This button is rendered inside fields
 * that live in forms, and the default for a `<button>` with no type is `submit`, so a
 * caller passing `type` — or simply reintroducing the spread order — turns "clear this
 * field" into "submit the form". `aria-label` is deliberately left overridable: a form
 * with several fields needs per-field names ("Clear Equals", "Clear Greater than"),
 * and that is the documented way to set them.
 */
export type InputClearProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;
export const InputClear = React.forwardRef<HTMLButtonElement, InputClearProps>(
  ({ className, onFocus, onBlur, ...props }, ref) => {
    // The field this button sits in, falling back to the group's primary for a
    // hand-built field (`InputControl` composed directly, with no `InputField`).
    // Reading the group first would send focus to whichever field mounted last.
    const field = React.useContext(InputFieldContext);
    const group = React.useContext(InputGroupContext);
    const controlRef = field?.controlRef ?? group?.controlRef;

    // Tracked rather than read at cleanup time: by then this button has left the
    // DOM and `document.activeElement` is already <body>, which is
    // indistinguishable from "nothing was focused anyway".
    const hadFocus = React.useRef(false);

    // Deliberately scoped to UNMOUNT, not to the click. The documented pattern
    // renders this button only while there is a value, so clearing destroys the
    // focused element and focus falls to <body> — a dead end for keyboard users.
    // Handing focus back on click instead would also move it when the button
    // stays mounted, which would break the guard that a press on an interactive
    // slot is never redirected to the control.
    React.useLayoutEffect(
      () => () => {
        if (hadFocus.current) controlRef?.current?.focus();
      },
      [controlRef]
    );

    return (
      <Button
        ref={ref}
        aria-label="Clear input"
        data-component="InputClear"
        className={cn('input-clear', className)}
        onFocus={(event) => {
          hadFocus.current = true;
          onFocus?.(event);
        }}
        onBlur={(event) => {
          // Cleared unconditionally. An earlier version only cleared when
          // `relatedTarget` was set, reasoning that a null one meant "this button is
          // being removed" — but null also means focus simply went nowhere (empty
          // space, `.blur()`, a tab switch, a <button> click on macOS Safari), so the
          // flag latched on and a LATER unmount stole focus into the field.
          //
          // Unconditional is safe because removing a focused element does not fire
          // blur: the flag survives exactly the case the cleanup below needs it for.
          // Both directions are pinned by tests — remove either and one goes red.
          hadFocus.current = false;
          onBlur?.(event);
        }}
        {...props}
        // AFTER the spread, deliberately: the type is a safety property, not a
        // default. The TS Omit above makes passing one an error, but a JS consumer
        // or a cast would still reach this, and the failure is silent — the field
        // clears and the form submits.
        type="button"
      >
        <X className="btn-icon" aria-hidden="true" />
      </Button>
    );
  }
);
InputClear.displayName = 'InputClear';

/**
 * InputTrailingIcon — shared trailing glyph slot (right-pinned). Compose
 * `.input-chevron` (dropdown) or `.input-lock` (disabled/locked) via className.
 */
export type InputTrailingIconProps = React.HTMLAttributes<HTMLSpanElement>;
export const InputTrailingIcon = React.forwardRef<HTMLSpanElement, InputTrailingIconProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      data-component="InputTrailingIcon"
      className={cn('input-trailing-icon', className)}
      aria-hidden="true"
      {...props}
    />
  )
);
InputTrailingIcon.displayName = 'InputTrailingIcon';

/** InputLabel — the field label. Wrap the label text in a `<span>` to enable truncation. */
export type InputLabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;
export const InputLabel = React.forwardRef<HTMLLabelElement, InputLabelProps>(
  ({ className, children, htmlFor, id, ...props }, ref) => {
    const group = React.useContext(InputGroupContext);
    const registerLabel = group?.registerLabel;
    // Always carries an id, so a button-shaped field and the popup it opens can name
    // themselves after it with `aria-labelledby`. A consumer's own id wins and is the
    // one published. A layout effect so the reference is in place before paint.
    const ownId = React.useId();
    const effectiveId = id ?? ownId;
    React.useLayoutEffect(() => registerLabel?.(effectiveId), [registerLabel, effectiveId]);

    return (
      <label
        ref={ref}
        id={effectiveId}
        htmlFor={htmlFor ?? group?.controlId}
        data-component="InputLabel"
        className={cn('input-label', className)}
        {...props}
      >
        <span>{children}</span>
      </label>
    );
  }
);
InputLabel.displayName = 'InputLabel';

/**
 * InputLabelRow — the `.input-label-row` wrapper that pairs an InputLabel with an
 * InputLabelInfo on one row, keeping the info trigger visible while long label
 * text truncates.
 */
export type InputLabelRowProps = React.HTMLAttributes<HTMLDivElement>;
export const InputLabelRow = React.forwardRef<HTMLDivElement, InputLabelRowProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="InputLabelRow"
      className={cn('input-label-row', className)}
      {...props}
    />
  )
);
InputLabelRow.displayName = 'InputLabelRow';

/** InputHelper — the helper / error / warning message row below the field. */
export type InputHelperProps = React.HTMLAttributes<HTMLDivElement>;
export const InputHelper = React.forwardRef<HTMLDivElement, InputHelperProps>(
  ({ className, children, id, ...props }, ref) => {
    const group = React.useContext(InputGroupContext);
    const registerHelper = group?.registerHelper;
    // Own id per helper, not the group's. The group id is the single-helper
    // convenience anchor; sharing it across several helpers put the same id on two
    // elements, so `aria-describedby` read "«r1» «r1»" and assistive tech resolved
    // both references to the first — silently dropping the second message.
    const ownId = React.useId();
    const effectiveId = id ?? ownId;

    // Announce the effective id so the control only advertises aria-describedby
    // when there is something to describe, and points at the right element even
    // when the consumer supplied their own id.
    React.useEffect(() => {
      if (!registerHelper || !effectiveId) return;
      return registerHelper(effectiveId);
    }, [registerHelper, effectiveId]);

    // Deliberately NOT `if (!children) return null`. The element stays mounted so
    // its id is a stable anchor: validation usually fails while the field is
    // focused, and several screen readers will not announce a description that
    // only appears after focus landed. `.input-helper:empty { display: none }`
    // collapses it, so an empty helper contributes no box and no flex gap.
    return (
      <div
        ref={ref}
        id={effectiveId}
        data-component="InputHelper"
        className={cn('input-helper', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
InputHelper.displayName = 'InputHelper';

/**
 * InputValueChange — the value-change affordance for an *editable* field. Rendered
 * in-flow immediately after the editable control (which holds the new value and
 * shrinks to its content), it shows `← oldValue unit` left-aligned — matching the
 * Figma "New" lockup (new → arrow → old). Pair with the `.input-new` root modifier
 * to highlight the control — note it does NOT switch sizing on. Content sizing comes
 * from this slot's presence, or from `autoSize` on `InputField`.
 */
export interface InputValueChangeProps extends React.HTMLAttributes<HTMLSpanElement> {
  oldValue: React.ReactNode;
  unit?: React.ReactNode;
}
export const InputValueChange = React.forwardRef<HTMLSpanElement, InputValueChangeProps>(
  ({ className, oldValue, unit, ...props }, ref) => (
    <span
      ref={ref}
      data-component="InputValueChange"
      className={cn('input-value-change', className)}
      {...props}
    >
      <ArrowBigLeft className="input-value-arrow" aria-hidden="true" />
      <span className="input-value-old">
        {/* The arrow carries the "was" meaning visually, but it is decorative and
            aria-hidden, so without this the old value reads as a bare number
            sitting beside the new one with nothing to say which is which. */}
        <span className="sr-only">Previous value </span>
        {oldValue}
        {unit ? <span className="input-unit">{unit}</span> : null}
      </span>
    </span>
  )
);
InputValueChange.displayName = 'InputValueChange';
